"use client";

import { cn } from "./cn";
import { SURFACES, type SurfaceId } from "./platform";

type AppSwitcherProps = {
  /** Die Flaeche, auf der der Nutzer gerade steht. */
  current: SurfaceId;
  /** Auf dunklem Grund (ERP-Sidebar) statt auf hellem (Portal-Kopf). */
  tone?: "light" | "dark";
  /** Flaechen, die dieser Nutzer nicht sehen darf, werden weggelassen. */
  available?: readonly SurfaceId[];
  className?: string;
};

/**
 * Der Wechsel zwischen den Flaechen der Plattform.
 *
 * Bewusst `<a>` statt `<Link>`: die Flaechen sind eigene Next-
 * Anwendungen hinter einem gemeinsamen Proxy. Ein Client-seitiger
 * Routenwechsel wuerde dort ins Leere laufen, und `<Link>` wuerde
 * im DAM zusaetzlich den basePath `/assets` davorsetzen.
 */
export function AppSwitcher({
  current,
  tone = "light",
  available,
  className,
}: AppSwitcherProps) {
  const surfaces = available
    ? SURFACES.filter((s) => available.includes(s.id))
    : SURFACES;

  if (surfaces.length < 2) return null;

  const dark = tone === "dark";

  return (
    <nav
      aria-label="Anwendung wechseln"
      className={cn(
        "flex items-center gap-1 rounded-md p-1",
        dark ? "bg-white/6" : "bg-bg-secondary",
        className
      )}
    >
      {surfaces.map((surface) => {
        const active = surface.id === current;
        return (
          <a
            key={surface.id}
            href={surface.href}
            aria-current={active ? "page" : undefined}
            title={surface.description}
            className={cn(
              "rounded-sm px-3 py-1.5 text-body-sm transition-colors duration-fast",
              active && dark && "bg-white/12 font-medium text-text-on-dark",
              active &&
                !dark &&
                "bg-bg-primary font-medium text-text-primary shadow-xs",
              !active &&
                dark &&
                "text-text-on-dark/50 hover:bg-white/6 hover:text-text-on-dark/90",
              !active &&
                !dark &&
                "text-text-secondary hover:text-text-primary"
            )}
          >
            {surface.label}
          </a>
        );
      })}
    </nav>
  );
}
